import generateUUID from 'lib/generate_uuid';

import { SurveyBuilderConstants as Constants } from 'components/survey_builder';
import {
  TEMPLATE_ITEM_TYPE_ANSWER,
  TEMPLATE_ITEM_TYPE_QUESTION,
  TEMPLATE_ITEM_TYPE_QUESTION_GROUP,
  ITEM_TYPES,
} from 'researcher/organization_management/components/organization_question_bank/constants';
import {
  TemplateAnswer,
  TemplateQuestion,
  TemplateQuestionGroup,
} from 'researcher/organization_management/components/organization_question_bank/types';

import { SectionUtilities } from './section_utilities';
import { SkipLogicConditionUtilities } from './skip_logic_condition_utilities';
import {
  Answer,
  FormType,
  Question,
  QuestionGroup,
  QuestionPick,
  QuestionType,
  Survey,
  SurveyUpdate,
} from './types';
import {
  buildAnswersForQuestion,
  buildAnswer,
  buildQuestion,
  buildQuestionGroup,
  insertIntoArray,
  isBooleanType,
  isPickNoneType,
  isPickOneOrAnyType,
  isPickOneOrAnyGridType,
  isQuestionGroup,
  removeFromArray,
  updateGridToGridQuestionType,
  updateGridQuestionType,
  updatePickQuestionType,
  updateNoPickToNoPickQuestionType,
  updatePickToPickQuestionType,
  updateNoPickQuestionType,
  removeQuestionAndAnswers,
} from './utilities';

const addQuestion = (
  sectionUUID: string,
  prevQuestionUUID: string,
  survey: Survey,
) => {
  const newQuestion = buildQuestion();
  const questions = { ...survey.questions };
  Object.keys(questions).forEach((uuid) => {
    questions[uuid] = { ...questions[uuid], isNew: false };
  });

  const section = { ...survey.sections[sectionUUID] };
  section.questionIds = insertIntoArray(
    section.questionIds,
    newQuestion.uuid,
    prevQuestionUUID,
  );

  const newAnswers = buildAnswersForQuestion(newQuestion);
  const answers = { ...survey.answers };
  newAnswers.forEach((answer) => {
    answers[answer.uuid] = answer;
    newQuestion.answerIds.push(answer.uuid);
  });

  return {
    answers,
    questions: { ...questions, [newQuestion.uuid]: newQuestion },
    sections: { ...survey.sections, [section.uuid]: section },
  };
};

// Moved without change from SurveyBuilder
// TODO: Improve/move this to where it's more applicable
const determineQuestionType = (question: Question, survey: Survey) => {
  if (question.pick === Constants.QUESTION_PICK_ONE) {
    if (isQuestionGroup(question)) return Constants.QUESTION_TYPE_PICK_ONE_GRID;

    return Constants.QUESTION_TYPE_PICK_ONE;
  }

  if (question.pick === Constants.QUESTION_PICK_ANY) {
    if (isQuestionGroup(question)) return Constants.QUESTION_TYPE_PICK_ANY_GRID;

    return Constants.QUESTION_TYPE_PICK_ANY;
  }

  if (question.pick === Constants.QUESTION_BOOLEAN) {
    return Constants.QUESTION_TYPE_BOOLEAN;
  }

  if (question.pick === Constants.QUESTION_PICK_NONE) {
    if (question.answerIds.length) {
      // If we have answers, refer to the first (and only) answer to determine the question type
      const answer = survey.answers[question.answerIds[0]];

      if (answer.responseClass === Constants.RESPONSE_CLASS_DATE) {
        return Constants.QUESTION_TYPE_DATE;
      }

      if (answer.responseClass === Constants.RESPONSE_CLASS_FLOAT) {
        return Constants.QUESTION_TYPE_FLOAT;
      }

      return answer.responseClass === Constants.RESPONSE_CLASS_TEXT
        ? Constants.QUESTION_TYPE_TEXT
        : Constants.QUESTION_TYPE_STRING;
    }

    // If there are no answers associated w/ this question, default to STRING type
    return Constants.QUESTION_TYPE_STRING;
  }

  throw TypeError(
    `Unexpected question pick found when determining question type: ${question.pick}`,
  );
};

const duplicateQuestion = (
  sectionUUID: string,
  questionUUID: string,
  survey: Survey,
  shouldPreserveAttributeMapping: boolean = false,
) => {
  const section = { ...survey.sections[sectionUUID] };
  const newQuestion = {
    ...survey.questions[questionUUID],
    aiGenerated: false,
    errors: {},
    isEditing: false,
    uuid: generateUUID(),
  } as Question | QuestionGroup;

  if (!shouldPreserveAttributeMapping) {
    delete newQuestion.participantPopulationAttributeId;
  }

  const answers = { ...survey.answers };
  const questions = { ...survey.questions };

  if (isQuestionGroup(newQuestion)) {
    // Question groups should not have participantPopulationAttributeId regardless of form type
    delete newQuestion.participantPopulationAttributeId;
    // Copy all the question's questions and put them into the new question map
    newQuestion.questionIds = newQuestion.questionIds.map((questionId) => {
      const newQuestionGroupQuestion = {
        ...survey.questions[questionId],
        aiGenerated: false,
        errors: {},
        questionGroupId: newQuestion.uuid,
        uuid: generateUUID(),
      } as Question;

      // Copy all the question group question's answers and put them into the new answers map
      newQuestionGroupQuestion.answerIds =
        newQuestionGroupQuestion.answerIds.map((answerId) => {
          const newAnswer = {
            ...survey.answers[answerId],
            errors: {},
            uuid: generateUUID(),
          };
          answers[newAnswer.uuid] = newAnswer;

          return newAnswer.uuid;
        });

      questions[newQuestionGroupQuestion.uuid] = newQuestionGroupQuestion;

      return newQuestionGroupQuestion.uuid;
    });
  } else {
    // Copy all the question's answers and put them into the new answers map
    newQuestion.answerIds = newQuestion.answerIds.map((answerId) => {
      const newAnswer = {
        ...survey.answers[answerId],
        errors: {},
        uuid: generateUUID(),
      };
      answers[newAnswer.uuid] = newAnswer;

      return newAnswer.uuid;
    });
  }

  // Insert the question after the previous question
  section.questionIds = insertIntoArray(
    section.questionIds,
    newQuestion.uuid,
    questionUUID,
  );

  return {
    answers,
    questions: { ...questions, [newQuestion.uuid]: newQuestion },
    sections: { ...survey.sections, [section.uuid]: section },
  };
};

const moveQuestionWithinSection = (
  { droppableId, index }: { droppableId: string; index: number },
  questionId: string,
  survey: Survey,
) => {
  const questionIds = [...survey.sections[droppableId].questionIds];
  const currentPosition = questionIds.indexOf(questionId);
  if (currentPosition === index) return survey;

  questionIds.splice(currentPosition, 1);
  questionIds.splice(index, 0, questionId);

  return {
    sections: {
      ...survey.sections,
      [droppableId]: {
        ...survey.sections[droppableId],
        questionIds,
      },
    },
  };
};

const moveQuestionFromSection = (
  source: { droppableId: string; index: number },
  destination: { droppableId: string; index: number },
  questionId: string,
  survey: Survey,
  formType: FormType,
): SurveyUpdate => {
  const currentSection = survey.sections[source.droppableId];
  const targetSection = survey.sections[destination.droppableId];

  // Create immutable copies of the questionIds arrays
  const currentSectionQuestionIds = [...currentSection.questionIds];
  const targetSectionQuestionIds = [...targetSection.questionIds];

  // Remove question from source section
  const currentPosition = currentSectionQuestionIds.indexOf(questionId);
  currentSectionQuestionIds.splice(currentPosition, 1);

  // Add question to target section
  targetSectionQuestionIds.splice(destination.index, 0, questionId);

  // Create updated sections with new questionIds arrays
  const updatedSections = {
    ...survey.sections,
    [source.droppableId]: {
      ...currentSection,
      questionIds: currentSectionQuestionIds,
    },
    [destination.droppableId]: {
      ...targetSection,
      questionIds: targetSectionQuestionIds,
    },
  };

  // Build the survey state with updated sections for skip logic cleanup
  let updatedSurvey = {
    ...survey,
    sections: updatedSections,
  };

  // Remove question's skip logic conditions
  const skipLogicUpdates = SkipLogicConditionUtilities.removeQuestionConditions(
    currentSection.uuid,
    questionId,
    updatedSurvey,
  );
  updatedSurvey = { ...updatedSurvey, ...skipLogicUpdates };

  // Remove origin section if last question
  if (currentSectionQuestionIds.length === 0) {
    const sectionRemovalUpdates = SectionUtilities.remove(
      currentSection.uuid,
      updatedSurvey,
      formType,
    );
    updatedSurvey = {
      ...updatedSurvey,
      ...sectionRemovalUpdates,
    };
  }

  return {
    sections: updatedSurvey.sections,
    sectionIds: updatedSurvey.sectionIds,
    skipLogics: updatedSurvey.skipLogics,
    skipLogicConditions: updatedSurvey.skipLogicConditions,
  };
};

const removeQuestion = (
  sectionUUID: string,
  questionUUID: string,
  survey: Survey,
) => {
  if (survey.sections[sectionUUID].questionIds.length === 1)
    return { ...survey };

  let updatedSurvey = { ...survey };
  let questions = { ...survey.questions };
  let answers = { ...survey.answers };
  const updatedSections = { ...survey.sections };
  const updatedSection = { ...survey.sections[sectionUUID] };

  // Remove one occurrence of question id from section question ids
  updatedSection.questionIds = removeFromArray(
    updatedSection.questionIds,
    questionUUID,
  );

  // Check if any references to this question remain in ANY section
  const hasRemainingReferences = Object.values({
    ...updatedSections,
    [sectionUUID]: updatedSection,
  }).some((section) => section.questionIds.includes(questionUUID));

  // Only delete the question object and answers if no references remain
  if (!hasRemainingReferences) {
    ({ answers, questions } = removeQuestionAndAnswers(
      survey.questions[questionUUID],
      updatedSurvey,
    ));
  }

  updatedSurvey = {
    ...updatedSurvey,
    answers,
    questions,
    sections: { ...updatedSections, [sectionUUID]: updatedSection },
  };

  // Only remove skip logic conditions if the question was fully removed
  if (!hasRemainingReferences) {
    // Clean up skip logic from ALL sections that might reference this question,
    // not just the section where the deletion was triggered. This handles the
    // case where a question exists in multiple sections and is removed from
    // a section without skip logic last - we still need to clean up skip logic
    // from other sections that referenced the now-deleted question.
    let skipLogicUpdates: SurveyUpdate = {};
    Object.keys(updatedSurvey.sections).forEach((sectionId) => {
      skipLogicUpdates = {
        ...skipLogicUpdates,
        ...SkipLogicConditionUtilities.removeQuestionConditions(
          sectionId,
          questionUUID,
          { ...updatedSurvey, ...skipLogicUpdates },
        ),
      };
    });

    return {
      ...updatedSurvey,
      ...skipLogicUpdates,
    };
  }

  return updatedSurvey;
};

const updateQuestion = (
  questionUUID: string,
  updates: Partial<Question>,
  survey: Survey,
) => {
  const question = {
    ...survey.questions[questionUUID],
    ...updates,
    errors: {},
  };

  return {
    questions: { ...survey.questions, [question.uuid]: question },
  };
};

const updateQuestionType = (
  questionUUID: string,
  questionType: QuestionType,
  survey: Survey,
): SurveyUpdate => {
  const question = survey.questions[questionUUID] as Question;

  const prevQuestionType = determineQuestionType(question, survey);

  if (questionType === prevQuestionType) return {};

  let update;

  if (isPickNoneType(questionType)) {
    if (isPickNoneType(prevQuestionType)) {
      update = updateNoPickToNoPickQuestionType(
        questionUUID,
        questionType,
        survey,
      );
    } else {
      update = updateNoPickQuestionType(questionUUID, questionType, survey);
    }
  }

  if (isPickOneOrAnyType(questionType)) {
    if (isPickOneOrAnyType(prevQuestionType)) {
      update = updatePickToPickQuestionType(questionUUID, questionType, survey);
    } else {
      update = updatePickQuestionType(questionUUID, questionType, survey);
    }
  }

  if (isBooleanType(questionType)) {
    update = updatePickQuestionType(questionUUID, questionType, survey);
  }

  if (isPickOneOrAnyGridType(questionType)) {
    if (isPickOneOrAnyGridType(prevQuestionType)) {
      update = updateGridToGridQuestionType(questionUUID, questionType, survey);
    } else {
      update = updateGridQuestionType(questionUUID, questionType, survey);
    }
  }

  if (!update || !update.questions) return {};
  // TODO Be smarter about this but at least we can pull this out of the child component.
  // We should be looking at the prev / next question type to ensure we only unset this if
  // it does not apply to the new type (e.g. we moved from Decimal to Boolean). Ideally
  // we'd have standard logic that looks at the attribute types / question types and does
  // that for us.
  delete update.questions[questionUUID].participantPopulationAttributeId;

  return {
    answers: update.answers,
    questions: update.questions,
  };
};

const addQuestionGroupFromTemplate = (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  templateItem: any,
  survey: Survey,
  lastSectionId: string,
) => {
  const questionGroupData = templateItem.item.find(
    (item: TemplateQuestionGroup) =>
      item.type === TEMPLATE_ITEM_TYPE_QUESTION_GROUP,
  );
  const questionData = templateItem.item.filter(
    (item: TemplateQuestion) => item.type === TEMPLATE_ITEM_TYPE_QUESTION,
  );
  const answerData = templateItem.item.filter(
    (item: TemplateAnswer) => item.type === TEMPLATE_ITEM_TYPE_ANSWER,
  );

  const randomizeAnswers = questionData.some(
    (q: TemplateQuestion) =>
      (q.attributes as Record<string, unknown>)?.randomizeAnswers === true,
  );

  const newQuestionGroup = buildQuestionGroup({
    text: questionGroupData.attributes.text,
    isMandatory: questionGroupData.attributes.isMandatory,
    pick: questionGroupData.attributes.pick as QuestionPick,
    randomizeAnswers,
  }) as QuestionGroup;

  // Create questions for the group, each with their own unique answers
  const newQuestions: { [key: string]: Question } = {};
  const newAnswers: { [key: string]: Answer } = {};

  questionData.forEach((question: TemplateQuestion) => {
    const newQuestion: Question = buildQuestion({
      text: question.attributes.text,
      isMandatory: question.attributes.isMandatory,
      pick: question.attributes.pick,
      questionGroupId: newQuestionGroup.uuid,
      randomizeAnswers:
        (question.attributes as Record<string, unknown>)?.randomizeAnswers ===
        true,
    });

    const questionAnswerIds =
      question.relationships?.answers?.data.map(
        (rel: { id: string }) => rel.id,
      ) ?? [];
    const questionAnswers = answerData.filter((answer: TemplateAnswer) =>
      questionAnswerIds.includes(answer.id),
    );

    questionAnswers.forEach((answer: TemplateAnswer) => {
      const newAnswer = buildAnswer(questionGroupData.attributes.pick, {
        text: answer.attributes.text,
        qualifyLogic: answer.attributes.qualifyLogic,
        displayOrder: answer.attributes.displayOrder,
      });

      newAnswers[newAnswer.uuid] = newAnswer;
      newQuestion.answerIds.push(newAnswer.uuid);
    });

    newQuestions[newQuestion.uuid] = newQuestion;
    newQuestionGroup.questionIds.push(newQuestion.uuid);
  });

  const updatedSection = { ...survey.sections[lastSectionId] };
  updatedSection.questionIds = insertIntoArray(
    updatedSection.questionIds,
    newQuestionGroup.uuid,
    '',
  );

  const mergedQuestions = {
    ...survey.questions,
    ...newQuestions,
    [newQuestionGroup.uuid]: newQuestionGroup,
  };

  return {
    answers: { ...survey.answers, ...newAnswers },
    questions: mergedQuestions,
    sections: { ...survey.sections, [lastSectionId]: updatedSection },
  };
};

const addQuestionFromTemplate = (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  templateItem: any,
  survey: Survey,
  shouldPreserveAttributeMapping: boolean = false,
) => {
  const sectionIds = survey.sectionIds;
  const lastSectionId = sectionIds[sectionIds.length - 1];

  if (templateItem.itemType === ITEM_TYPES.QUESTION_GROUP) {
    return addQuestionGroupFromTemplate(templateItem, survey, lastSectionId);
  }

  const questionData = templateItem.item.find(
    (item: TemplateQuestion) => item.type === TEMPLATE_ITEM_TYPE_QUESTION,
  );
  const answerData = templateItem.item.filter(
    (item: TemplateAnswer) => item.type === TEMPLATE_ITEM_TYPE_ANSWER,
  );

  const newQuestion: Question = buildQuestion({
    text: questionData.attributes.text,
    isMandatory: questionData.attributes.isMandatory,
    pick: questionData.attributes.pick,
    questionGroupId: questionData.attributes.questionGroupId,
    participantPopulationAttributeId: shouldPreserveAttributeMapping
      ? questionData.attributes.participantPopulationAttributeId
      : undefined,
    randomizeAnswers:
      (questionData.attributes as Record<string, unknown>)?.randomizeAnswers ===
      true,
  });

  const newAnswers: { [key: string]: Answer } = {};
  answerData.forEach((answer: TemplateAnswer) => {
    const newAnswer = buildAnswer(questionData.attributes.pick, {
      text: answer.attributes.text,
      responseClass: answer.attributes.responseClass,
      qualifyLogic: answer.attributes.qualifyLogic,
      displayOrder: answer.attributes.displayOrder,
    });

    newAnswers[newAnswer.uuid] = newAnswer;
    newQuestion.answerIds.push(newAnswer.uuid);
  });

  const updatedSection = { ...survey.sections[lastSectionId] };
  updatedSection.questionIds = insertIntoArray(
    updatedSection.questionIds,
    newQuestion.uuid,
    '',
  );

  return {
    answers: { ...survey.answers, ...newAnswers },
    questions: { ...survey.questions, [newQuestion.uuid]: newQuestion },
    sections: { ...survey.sections, [lastSectionId]: updatedSection },
  };
};

export const QuestionUtilities = {
  add: addQuestion,
  addFromTemplate: addQuestionFromTemplate,
  determineType: determineQuestionType,
  duplicate: duplicateQuestion,
  moveFromSection: moveQuestionFromSection,
  moveWithinSection: moveQuestionWithinSection,
  remove: removeQuestion,
  update: updateQuestion,
  updateType: updateQuestionType,
};
